import React, { useCallback, useEffect, useMemo } from 'react';

import type { Field, FieldError } from './useField';

interface FormOptions {
  highlightErrors?: boolean;
}

export function useForm(fields: Field<UnsafeAny>[], onSubmit: () => Promise<void>, options?: FormOptions) {
  const mounted = React.useRef(false);
  const [state, setState] = React.useState({
    submitting: false,
    submitted: false,
  });

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (fields.some(it => !!it.error)) {
        setState({ submitting: false, submitted: true });
        if (options?.highlightErrors) {
          const firstError = document.querySelector('.error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }
      setState(prev => ({ ...prev, submitting: true }));
      await onSubmit();
      if (mounted.current) {
        setState({ submitting: false, submitted: true });
      }
    },
    [fields, onSubmit, options],
  );

  const errors = useMemo(() => {
    const result: Record<string, FieldError> = {};
    for (const field of fields) {
      if (field.error && (field.touched || state.submitted)) {
        result[field.name] = field.error;
      }
    }
    return result;
  }, [fields, state.submitted]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  });

  const form = {
    ...state,
    errors,
  };

  return [handleSubmit, form] as const;
}
